// Home.js
import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import useInactivityHandler from "../utils/InactivityHandler";
import axios from "axios";
import { PuffLoader } from "react-spinners";
import { useAuth } from "../context/AuthContext";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import API_ENDPOINTS from "../config/api";
// import { FaWhatsapp } from "react-icons/fa";    // ← switched to react-icons

const Home = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { token, fullname, account, phoneNumber, isLoading: authLoading } = useAuth();
  const [balance, setBalance] = useState("");
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  useInactivityHandler();

  useEffect(() => {
    const disableRightClick = (e) => e.preventDefault();
    document.addEventListener("contextmenu", disableRightClick);
    return () => document.removeEventListener("contextmenu", disableRightClick);
  }, []);

  // Detect refresh flag from navigation
  useEffect(() => {
    if (location.state?.refresh) {
      setRefreshKey(prev => prev + 1);
      // Clear the state to prevent re-triggering
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate]);

  useEffect(() => {
    const fetchData = async () => {
      // Wait for auth to load before making API calls
      if (authLoading) {
        return;
      }

      setLoading(true);
      try {
        if (token) {
          const balanceResponse = await axios.post(
            API_ENDPOINTS.GET_BALANCE,
            { account, token }
          );
          const fetchedBalance = balanceResponse.data.balance;
          setBalance(fetchedBalance === 0 ? "0.00" : fetchedBalance || "N/A");

          const transactionResponse = await axios.post(
            API_ENDPOINTS.GET_TRANSACTIONS,
            { token }
          );
          setTransactions(transactionResponse.data.transactions || []);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
      setLoading(false);
    };
    fetchData();
  }, [token, account, authLoading, refreshKey]);

  const handleDeposit = () => navigate("/deposit");
  const handleWithdraw = () => navigate("/withdraw", { state: { balance } });

  return (
    <div className="flex-1 p-4 sm:p-6">
      <ToastContainer />

      {/* WhatsApp banner */}
      {/* <div className="mb-4 rounded-lg bg-green-500 text-white">
        <a
          href="https://www.whatsapp.com/channel/0029VbASapoDp2Q0Lic1I73k"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 px-4 py-3 justify-center sm:justify-start"
        >
          <FaWhatsapp className="h-5 w-5 shrink-0" />
          <span className="font-semibold text-sm sm:text-base">
            Click to Join our WhatsApp Channel and Print Dollars 💲💲
          </span>
        </a>
      </div> */}

      <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold mb-4 text-center sm:text-left">
        Welcome Back, {fullname} {phoneNumber}
      </h1>

      <div className="mb-6 p-4 bg-pink-600 text-white rounded-lg text-center sm:text-left">
        <h2 className="text-lg sm:text-xl">
          {account} Balance: {balance} USD
        </h2>
      </div>

      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <button
          onClick={handleDeposit}
          className="px-4 py-2 text-sm sm:text-base bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition w-full sm:w-auto"
        >
          Deposit
        </button>
        <button
          onClick={handleWithdraw}
          className="px-4 py-2 text-sm sm:text-base bg-red-500 text-white rounded-lg hover:bg-red-600 transition w-full sm:w-auto"
        >
          Withdraw
        </button>
      </div>

      <h2 className="text-lg sm:text-xl font-semibold mb-4">
        Recent Transactions
      </h2>

      {loading ? (
        <div className="flex justify-center">
          <PuffLoader color="#36D7B7" size={60} />
        </div>
      ) : (
        <>
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <ul className="divide-y divide-gray-200">
              {transactions.map((tx, i) => (
                <li
                  key={i}
                  className="p-2 sm:p-4 flex flex-col sm:flex-row justify-between items-center"
                >
                  <span
                    className={`font-semibold ${
                      tx.action_type === "deposit" ? "text-green-500" : "text-red-500"
                    }`}
                  >
                    {tx.action_type === "deposit" ? "Deposit" : "Withdraw"}
                  </span>
                  <span className="text-sm sm:text-base">{tx.amount} USD</span>
                  <span className="text-gray-600 text-xs sm:text-sm">
                    {new Date(tx.transaction_time * 1000).toLocaleDateString()}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* Telegram Channel Button */}
          <div className="mt-6">
            <a
              href="https://t.me/money8ggtrades"
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full sm:w-auto sm:mx-auto"
            >
              <button className="w-full sm:max-w-md px-6 py-3 bg-blue-500 hover:bg-blue-600 text-white font-semibold rounded-lg shadow-md transition-all duration-200 transform hover:scale-105 flex items-center justify-center gap-2 text-sm sm:text-base">
                <svg
                  className="w-5 h-5 sm:w-6 sm:h-6"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.562 8.161c-.18 1.897-.962 6.502-1.359 8.627-.168.9-.5 1.201-.82 1.23-.697.064-1.226-.461-1.901-.903-1.056-.693-1.653-1.124-2.678-1.8-1.185-.781-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.139-5.062 3.345-.479.329-.913.489-1.302.481-.428-.008-1.252-.241-1.865-.44-.752-.244-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.831-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635.099-.002.321.023.465.141.121.099.155.232.171.325.016.093.036.305.02.469z"/>
                </svg>
                <span>Join Telegram Channel for Strategies</span>
              </button>
            </a>
          </div>
        </>
      )}
    </div>
  );
};

export default Home;
